"use client";

import styles from "./index.module.css";
import { useHistoryManager } from "../history/useHistoryManager";
import {
  getAccessPoint,
  getAccessPointId,
  getAccessPoints,
  getOrganisation,
} from "../slice/accessOrg-slice";
import { useAppSelector } from "../slice/hooks";
import toast from "react-hot-toast";
import { useState } from "react";
import { buildQueryString } from "../../lib/util";

const ProductFilterPanel = ({
  setSelectedFilterColors,
  setSelectedFilterPrice,
  setSelectedFilterSizes,
  setSelectedFilterCategory,
  setSelectedFilterCountry,
  selectedFilterPrice,
  selectedFilterColors,
  selectedFilterSizes,
  selectedFilterCategory,
  selectedFilterCountry,
  filterData,
}) => {
  const { navigateTo } = useHistoryManager();
  const organisation = useAppSelector(getOrganisation);
  const accessPointId = useAppSelector(getAccessPointId);
  const accessPoint = useAppSelector(getAccessPoint);
  const accessPoints = useAppSelector(getAccessPoints);
  const [minInput, setMinInput] = useState("");
  const [maxInput, setMaxInput] = useState("");

  /* ---------------- FILTER STATE ---------------- */

  const updateSingleFilterInUrl = (key, value) => {
    // Normalize value (handle arrays & empty cases)
    let normalizedValue = value;

    if (
      value === null ||
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
    ) {
      normalizedValue = null; // buildQueryString will delete it
    } else if (Array.isArray(value)) {
      normalizedValue = value.join(",");
    }

    // Build updated query string (auto-merges existing params)
    const urlQuery = buildQueryString({
      [key]: normalizedValue,
    });

    navigateTo(`${window.location.pathname}${urlQuery}`, {
      urlData: {
        organisation,
        accessPointId,
        accessPoint,
        accessPoints,
      },
      replace: true,
    });
  };

  const toggleColor = (color) => {
    setSelectedFilterColors((prev) => {
      const updated = prev.includes(color)
        ? prev.filter((c) => c !== color)
        : [...prev, color];

      updateSingleFilterInUrl("colors", updated);
      return updated;
    });
  };

  const toggleSize = (size) => {
    setSelectedFilterSizes((prev) => {
      const updated = prev.includes(size)
        ? prev.filter((s) => s !== size)
        : [...prev, size];

      updateSingleFilterInUrl("sizes", updated);
      return updated;
    });
  };

  const clearAllFilters = () => {
    setSelectedFilterColors([]);
    setSelectedFilterSizes([]);
    setSelectedFilterCategory(null);
    setSelectedFilterCountry(null);
    setSelectedFilterPrice({ min: null, max: null });
    setMinInput("");
    setMaxInput("");

    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    ["colors", "sizes", "category", "country", "minPrice", "maxPrice"].forEach(
      (key) => params.delete(key),
    );

    const newQuery = params.toString();
    const finalUrl = newQuery ? `${url.pathname}?${newQuery}` : url.pathname;

    navigateTo(finalUrl, {
      urlData: {
        organisation,
        accessPointId,
        accessPoint,
        accessPoints,
      },
      replace: true,
    });
  };

  const hasActiveFilters =
    selectedFilterColors.length > 0 ||
    selectedFilterSizes.length > 0 ||
    selectedFilterCategory !== null ||
    selectedFilterCountry !== null ||
    selectedFilterPrice.min !== null ||
    selectedFilterPrice.max !== null ||
    minInput !== "" ||
    maxInput !== "";

  return (
    <div className={styles.filterPanel}>
      <div className={styles.filterHeaderRow}>
        <h3>Filter</h3>

        {hasActiveFilters && (
          <button
            type="button"
            className={styles.clearSmallButton}
            onClick={clearAllFilters}
          >
            Clear All
          </button>
        )}
      </div>

      {/* CATEGORY */}
      {filterData?.productCategoryNames?.length > 0 && (
        <div className={styles.filterSection}>
          <h4>Category</h4>
          {filterData?.productCategoryNames?.map((cat) => (
            <button
              key={cat}
              className={`${styles.filterButton} ${
                selectedFilterCategory === cat ? styles.filterActive : ""
              }`}
              onClick={() => {
                const newCategory = selectedFilterCategory === cat ? null : cat;

                setSelectedFilterCategory(newCategory);
                updateSingleFilterInUrl("category", newCategory);
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* PRICE INPUTS */}
      <div className={styles.filterSection}>
        <div className={styles.filterHeaderRow}>
          <h4>Price</h4>

          <button
            type="button"
            className={styles.clearSmallButton}
            onClick={() => {
              setMinInput("");
              setMaxInput("");

              setSelectedFilterPrice({
                min: null,
                max: null,
              });

              updateSingleFilterInUrl("minPrice", null);
              updateSingleFilterInUrl("maxPrice", null);
            }}
          >
            Clear
          </button>
        </div>

        <div className={styles.priceRow}>
          <div>
            <input
              type="number"
              placeholder="Min"
              value={minInput}
              onChange={(e) => setMinInput(e.target.value)}
              className={styles.priceInput}
            />
          </div>

          <div>
            <input
              type="number"
              placeholder="Max"
              value={maxInput}
              onChange={(e) => setMaxInput(e.target.value)}
              className={styles.priceInput}
            />
          </div>

          <button
            className={styles.goButton}
            disabled={!minInput && !maxInput}
            onClick={() => {
              const parsedMin = minInput ? Number(minInput) : null;
              const parsedMax = maxInput ? Number(maxInput) : null;

              if (
                parsedMin !== null &&
                parsedMax !== null &&
                parsedMin > parsedMax
              ) {
                toast.error(
                  "Minimum price cannot be greater than maximum price",
                );
                return;
              }

              if (!parsedMax || !parsedMin) {
                toast.error("Fill In the value");
                return;
              }

              // 🔥 ONLY update real filter state here
              setSelectedFilterPrice({
                min: parsedMin,
                max: parsedMax,
              });

              updateSingleFilterInUrl("minPrice", parsedMin);
              updateSingleFilterInUrl("maxPrice", parsedMax);
            }}
          >
            Go
          </button>
        </div>
      </div>

      {/* COLOR */}
      {filterData?.colors?.length > 0 && (
        <div className={styles.filterSection}>
          <div className={styles.filterHeaderRow}>
            <h4>Colour</h4>

            {selectedFilterColors.length > 0 && (
              <button
                type="button"
                className={styles.clearSmallButton}
                onClick={() => {
                  setSelectedFilterColors([]);
                  updateSingleFilterInUrl("colors", []);
                }}
              >
                Clear
              </button>
            )}
          </div>

          <div className={styles.scrollContainer}>
            {filterData?.colors?.map((color) => {
              const isSelected = selectedFilterColors.includes(color.name);

              return (
                <div
                  key={color._id}
                  className={`${styles.colorRow} ${
                    isSelected ? styles.filterActive : ""
                  }`}
                  onClick={() => toggleColor(color.name)}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                    className={styles.colorCheckbox}
                  />

                  <span className={styles.colorName}>{color.name}</span>

                  <span
                    className={styles.colorPreview}
                    style={{ backgroundColor: color.code }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SIZE */}
      {filterData.sizes?.length > 0 && (
        <div className={styles.filterSection}>
          <div className={styles.filterHeaderRow}>
            <h4>Sizes</h4>

            {selectedFilterSizes.length > 0 && (
              <button
                type="button"
                className={styles.clearSmallButton}
                onClick={() => {
                  setSelectedFilterSizes([]);
                  updateSingleFilterInUrl("sizes", []);
                }}
              >
                Clear
              </button>
            )}
          </div>
          <div className={styles.scrollContainer}>
            {filterData?.sizes?.map((size) => (
              <button
                key={size}
                type="button"
                className={`${styles.filterButton} ${
                  selectedFilterSizes.includes(size) ? styles.filterActive : ""
                }`}
                onClick={() => toggleSize(size)}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* COUNTRY */}
      {filterData.countries?.length > 0 && (
        <div className={styles.filterSection}>
          <h4>Country</h4>
          {filterData?.countries?.map((country) => (
            <button
              key={country}
              className={`${styles.filterButton} ${
                selectedFilterCountry === country ? styles.filterActive : ""
              }`}
              onClick={() => {
                const newCountry =
                  selectedFilterCountry === country ? null : country;
                setSelectedFilterCountry(newCountry);
                updateSingleFilterInUrl("country", newCountry);
              }}
            >
              {country}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductFilterPanel;
